import { Link } from "react-router-dom";
import { ArrowLeft, Trophy, Flame, Pill, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";
import { BadgeList } from "../components/teams/Badges";
import { Avatar } from "../components/Avatar";
import { walletOf } from "../lib/coins";
import { Coins } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const { progress, isGuest, loaded } = useProgress();
  const stats = progress?.stats || {};
  const ekg = progress?.ekg || {};
  const meds = progress?.medication || {};

  const hardCleared = Object.values(ekg).filter((e) => e?.cleared_hardcore).length;
  const wallet = walletOf(stats);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      <div className="panel p-8 mb-8 flex flex-col sm:flex-row sm:items-center gap-6">
        {loaded ? <Avatar id={wallet.avatar} bg={wallet.bg} size={120} ring animated testid="profile-avatar" /> : <div style={{ width: 120, height: 120 }} className="border border-border animate-pulse" />}
        <div className="flex-1">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// OPERATOR</div>
        <h1 className="text-4xl font-extrabold tracking-tight" data-testid="profile-name">{isGuest ? "Guest" : user?.name}</h1>
        <div className="text-sm text-muted-foreground mt-1">{isGuest ? "Progress is stored on this device only." : user?.email}</div>
        {isGuest && (
          <Link to="/auth" data-testid="profile-signin" className="inline-block mt-5 bg-primary text-primary-foreground px-5 py-2 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-primary/90">
            Sign in to save progress
          </Link>
        )}
        </div>
        <Link to="/avatars" data-testid="profile-coins" className="text-right border border-border hover:border-amber-300 p-4">
          <div className="text-[10px] tracking-widest text-muted-foreground">COINS</div>
          <div className="text-3xl font-extrabold text-amber-300 tabular-nums inline-flex items-center gap-2"><Coins className="w-6 h-6" /> {wallet.coins}</div>
          <div className="text-[10px] text-muted-foreground">{wallet.owned.length} avatar{wallet.owned.length === 1 ? "" : "s"} collected</div>
        </Link>
      </div>

      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <Metric icon={Flame} label="Streak" value={stats.streak || 0} />
        <Metric icon={Trophy} label="EKG Hardcore Clears" value={`${hardCleared} / 6`} accent />
        <Metric icon={Activity} label="Highest Level" value={stats.ekg_highest_level || 1} />
        <Metric icon={Pill} label="Med Best · Easy" value={stats.med_best_score_easy || 0} />
        <Metric icon={Pill} label="Med Best · Hardcore" value={stats.med_best_score || 0} accent />
      </div>

      <div className="panel p-6 mb-6" data-testid="ekg-badges">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">EKG Badges</h2>
          <div className="flex items-center gap-4 text-[10px] tracking-widest text-muted-foreground">
            <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-phosphor" /> EASY</span>
            <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-amber-300" /> HARDCORE</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1, 2, 3, 4, 5, 6].map((lvl) => {
            const d = ekg[lvl] || {};
            const easy = !!d.cleared_easy;
            const hard = !!d.cleared_hardcore;
            return (
              <div
                key={lvl}
                data-testid={`badge-${lvl}`}
                data-easy={easy}
                data-hardcore={hard}
                className={`border p-3 flex flex-col items-center ${
                  hard ? "border-amber-400/70 bg-amber-400/5 hardcore-badge" : easy ? "border-phosphor" : "border-border opacity-50"
                }`}
              >
                <div className="text-[10px] tracking-widest text-muted-foreground mb-2">LVL {lvl}</div>
                <div className="flex items-center gap-2">
                  <Trophy className={`w-6 h-6 ${easy ? "text-phosphor" : "text-muted-foreground/40"}`} data-testid={`badge-${lvl}-easy`} />
                  <Trophy className={`w-7 h-7 ${hard ? "text-amber-300 drop-shadow-[0_0_8px_rgba(252,211,77,0.8)]" : "text-muted-foreground/40"}`} data-testid={`badge-${lvl}-hardcore`} />
                </div>
                <div className="mt-2 text-[9px] text-muted-foreground tabular-nums">
                  <span className={easy ? "text-phosphor" : ""}>{d.best_easy ?? 0}</span>
                  <span className="mx-1">·</span>
                  <span className={hard ? "text-amber-300" : ""}>{d.best_hardcore ?? 0}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!isGuest && (
        <div className="mb-6">
          <BadgeList endpoint="/clans/badges" title="Clan Trophies" testid="clan-badges"
            emptyText="No clan trophies yet. Win a clan challenge window with your team and the badge lands here for every member." />
        </div>
      )}

      <div className="panel p-6">
        <h2 className="text-xl font-bold mb-4">Medication Mastery</h2>
        {Object.keys(meds).length === 0 ? (
          <div className="text-sm text-muted-foreground">No sessions yet. Start a drill from the dashboard.</div>
        ) : (
          <div className="space-y-2">
            {Object.entries(meds).map(([classId, m]) => {
              const total = Object.keys(m?.weights || {}).length;
              const mastered = Object.values(m?.mastery || {}).filter((v) => v >= 2).length;
              return (
                <div key={classId} className="flex items-center justify-between border border-border p-3">
                  <div className="text-sm font-semibold">{classId.replace(/_/g, " ").toUpperCase()}</div>
                  <div className="text-xs text-muted-foreground">
                    {mastered} mastered of {total} seen
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className={`panel p-4 flex items-center gap-3 ${accent ? "border-amber-400/40" : ""}`}>
      <Icon className={`w-6 h-6 ${accent ? "text-amber-300" : "text-phosphor"}`} />
      <div>
        <div className="text-[10px] tracking-widest text-muted-foreground">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </div>
    </div>
  );
}
